
import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import { setUpdateHold } from "@/utils/UpdateHold";
import { GenericDevice, NewGenericDevice } from "@/data/models/GenericDevice";
import ClientPanel from "@/data/models/ClientPanel";
import { getAvailablePanels } from "@/data/repository/ClientPanelRepository";
import {
  CSV_REPORT_HEADERS,
  decodeCSVBuffer,
  isCSVFile,
  MAX_EXPECTED_COLUMNS,
  MIN_EXPECTED_COLUMNS,
  parseLine,
} from "@/utils/devices.csv";
import {
  getClientDevices
} from "@/data/repository/GenericDeviceRepository";
import { DeviceImporter } from "@/utils/DeviceImporter";
import ImportHelpDialog from "@/components/settings/devices/import/ImportHelpDialog.vue";
import { CSV_SEPARATOR, getFileNameWithoutExtension } from "@/utils/Utils";
import { getClientDeviceSettingsDeprecated } from "@/data/repository/ClientRepository";
import { ImportError } from "@/data/models/CSV";
import { GenericDeviceSettingsDeprecated } from "@/data/models/Client";
import { firestore } from "firebase";
import { timestampToDate } from "@/utils/DateFormatter";
import { getFormattedDeviceString } from "@/utils/DeviceManagementUtils";
import { ToolbarContentModule } from "@/store/modules/ToolbarContent";
import { defineCategoriesAndTemplatesClientAccess } from "@/permissions/ClientPermissions";
import { DeviceCategoriesAndTemplatesModule } from "@/store/modules/DeviceCategoriesAndTemplatesModule";
import { AllDevicesSettingsModule } from "@/store/modules/AllDevicesSettingsModule";
import {
  COLOR_ALL_DEVICES_IMPORT_UPLOAD_BOX_BACKGROUND,
  COLOR_ALL_DEVICES_IMPORT_UPLOAD_BOX_BACKGROUND_DARK,
  COLOR_ALL_DEVICES_IMPORT_UPLOAD_BOX_BORDER,
  COLOR_ALL_DEVICES_IMPORT_UPLOAD_BOX_BORDER_DARK,
  COLOR_BUTTON_YELLOW,
} from "@/utils/colors";
import { themed, themedFill, themedFillTextClass } from "@/utils/Theme";
import { mapDeviceCsvToSection } from "@/utils/DeviceCsvToViewer";
import { ViewerSection } from "@/components/common/data-viewer/DataViewerModels";
import DataViewerComponent from "@/components/common/data-viewer/DataViewer.vue";
import { DownloadFileType, getFileTypeIcon } from "@/utils/FileTypeIcons";

@Component({
  components: {
    ImportHelpDialog,
    "data-viewer": DataViewerComponent,
  },
})

export default class DeviceImport extends Vue {
  @Ref('upload')
  uploadElement!: HTMLInputElement
  fileName: string | null = ''
  clientPanels: ClientPanel[] = []
  devices: GenericDevice[]
  allDevices: (GenericDevice | ClientPanel)[]
  csvContents: string;
  file: File | null = null;
  progress = 0;
  newDevices = 0;
  updatedDevices = 0;
  parsedDevices = 0;
  processedDevices = 0;
  duplicatedDevices = 0;
  errors: ImportError[] = [];
  processing = false;
  importCanceled = false;
  importJob: DeviceImporter | null = null;
  parsingFile = false;
  importReport: string[];
  criticalFailure = false;
  errorSnackbar = false;
  errorSnackbarTimeout = 5000;
  errorSnackbarText = "";
  showHelpDialog = false
  componentActive = false;
  isLoading = true;
  previewComplete = false;
  previewSections: ViewerSection[] = [];
  private parsedData: NewGenericDevice[] = [];
  private cachedClientSettings: GenericDeviceSettingsDeprecated | null = null;

  get isAllowedToDefineOwnDeviceCategoriesAndTemplates() {
    return defineCategoriesAndTemplatesClientAccess();
  }

  get sharedDeviceSettingsJson() {
    return DeviceCategoriesAndTemplatesModule.sharedDeviceCategoriesAndTemplates;
  }

  get localDeviceSettingsJson() {
    return DeviceCategoriesAndTemplatesModule.localDeviceCategoriesAndTemplates;
  }

  get deviceSettingsJson() {
    return this.isAllowedToDefineOwnDeviceCategoriesAndTemplates
      ? this.localDeviceSettingsJson
      : this.sharedDeviceSettingsJson;
  }

  get buttonColor() {
    return themedFill(COLOR_BUTTON_YELLOW);
  }

  get buttonTextClass() {
    return themedFillTextClass(COLOR_BUTTON_YELLOW, "white--text");
  }

  get uploadBoxBorderColor() {
    return themed(COLOR_ALL_DEVICES_IMPORT_UPLOAD_BOX_BORDER, COLOR_ALL_DEVICES_IMPORT_UPLOAD_BOX_BORDER_DARK);
  }

  get uploadBoxBackgroundColor() {
    return themed(COLOR_ALL_DEVICES_IMPORT_UPLOAD_BOX_BACKGROUND, COLOR_ALL_DEVICES_IMPORT_UPLOAD_BOX_BACKGROUND_DARK);
  }

  get parsedDevicesString() {
    return getFormattedDeviceString(this, this.parsedDevices, "settings.hyDryDevice.devicesDetected");
  }

  get processedDevicesString() {
    return getFormattedDeviceString(this, this.processedDevices, "settings.hyDryDevice.devicesProcessed");
  }

  get newDevicesString() {
    return getFormattedDeviceString(this, this.newDevices, "settings.hyDryDevice.devicesNew");
  }

  get updatedDevicesString() {
    return getFormattedDeviceString(this, this.updatedDevices, "settings.hyDryDevice.devicesUpdated");
  }

  get duplicatedDevicesString() {
    return getFormattedDeviceString(this, this.duplicatedDevices, "settings.hyDryDevice.devicesDuplicated");
  }

  get failedDevicesString() {
    return getFormattedDeviceString(this, this.errors.length, "settings.hyDryDevice.deviceError");
  }

  async created() {
    await this.loadDevicesAndSettings();
    this.componentActive = true;
  }

  async loadDevicesAndSettings() {
    this.isLoading = true;
    await this.refreshDevicesAndSettings();
    this.isLoading = false;
  }

  /** Reloads devices and panels without showing the full-screen loader. */
  private async refreshDevicesAndSettings() {
    this.clientPanels = await getAvailablePanels();
    this.devices = await getClientDevices();
  }

  showSnackbar(message: string) {
    this.errorSnackbar = true
    this.errorSnackbarText = message
    const helpIcon = document.querySelector('.help-icon');

    helpIcon?.classList.add('animated');

    setTimeout(() => {
      if (helpIcon) {
        helpIcon.classList.remove('animated');
      }
    }, 3000);
  }

  hideSnackbar() {
    this.errorSnackbar = false
    this.errorSnackbarText = ""
  }


  handleFileUpload(droppedFile?: File) {
    if (!this.uploadElement.files) {
      this.showSnackbar("settings.hyDryDevice.errorMessageTryAgain")
      return;
    }
    const file = this.uploadElement.files[0] ?? droppedFile
    if (!isCSVFile(file)) {
      this.showSnackbar("settings.hyDryDevice.errorMessageWrongFormat")
      return;
    }
    const MAX_FILE_SIZE_MB = 5;
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      this.showSnackbar("settings.hyDryDevice.errorMessageFileTooLarge")
      return;
    }
    this.file = file
    this.fileName = file.name
    const reader = new FileReader();
    
    reader.onload = (e) => {
      if (!e.target || !e.target.result) {
        return;
      }
      this.csvContents = decodeCSVBuffer(e.target.result as ArrayBuffer);
    };

    reader.readAsArrayBuffer(file);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleDrop(event: any) {
    event.preventDefault();
    if (event.dataTransfer.items) {
      // Use DataTransferItemList interface to access the file(s)
      const fileItem = event.dataTransfer.items[0];

      if (fileItem.kind === "file") {
        const file: File = fileItem.getAsFile();
        this.handleFileUpload(file)
      }
    } else {
      // Use DataTransfer interface for browsers that don't support DataTransferItemList
      const file = event.dataTransfer.files[0];
      this.handleFileUpload(file)
    }
  }

  formatFileSize() {
    const file = this.file
    if (!file) {
      return
    }
    let size = file.size / 1024
    const units = ['KB', 'MB'];
    let unitIndex = 0;
    while (size > 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    return `${size.toFixed(2)} ${units[unitIndex]}`;
  }

  get importConfiguration() {
    return AllDevicesSettingsModule.allDevicesSettings.importConfiguration;
  }

  async processCSV() {
    const lines = this.csvContents.split('\n');
    const data: NewGenericDevice[] = [];

    const importConfiguration = this.importConfiguration;
    const clientSettings = await getClientDeviceSettingsDeprecated() || {
      newCategoryAllowed: false,
      newTemplateAllowed: false,
      updateAllowed: false,
    };

    if (importConfiguration && !importConfiguration.defaultImportConfiguration) {
      clientSettings.newCategoryAllowed = importConfiguration.newCategoryAllowed,
      clientSettings.newTemplateAllowed = importConfiguration.newTemplateAllowed,
      clientSettings.updateAllowed = importConfiguration.updateAllowed
      
    }

    this.processing = true;
    try {
      const headerLine = lines[0]
      const isValidSeparator = headerLine.includes(CSV_SEPARATOR)  
      
      if (!isValidSeparator) {
        this.cancelUpload()
        this.resetFile()
        this.showSnackbar('settings.hyDryDevice.errorMessageWrongCsvSeparator')
        return;
      }

      const columnLength = lines[0].split(CSV_SEPARATOR).length;
      if (columnLength < MIN_EXPECTED_COLUMNS) {
        this.cancelUpload()
        this.resetFile()
        
        this.showSnackbar(columnLength === 3
          ? 'settings.hyDryDevice.errorMessageMissingSerialNumberColumn'
          : columnLength === 2
            ? 'settings.hyDryDevice.errorMessageMissingInventoryNumberColumn'
            : columnLength === 1
              ? 'settings.hyDryDevice.errorMessageMissingTemplateColumn'
              : 'settings.hyDryDevice.errorMessageMissingCategoryColumn'
        )
        
        return;
      }

      const noOfColumnsToBeConsidered = columnLength > MAX_EXPECTED_COLUMNS
        ? MAX_EXPECTED_COLUMNS
        : columnLength;
      
      this.parsingFile = true
      for (let index = 1; index < lines.length; index++) {
        if (this.importCanceled) {
          break;
        }
        try {
          const currentLine = lines[index].split(CSV_SEPARATOR);
          if (this.deviceSettingsJson && currentLine.length > 1) {
            const parsedData = parseLine(
              this.deviceSettingsJson, 
              currentLine,
              noOfColumnsToBeConsidered
            );

            data.push(parsedData);
            this.parsedDevices = data.length;
          }
        } catch (e) {
          this.cancelUpload()
          this.showSnackbar('settings.hyDryDevice.errorMessageTryAgain')
          break;
        }
      }

      this.parsingFile = false
      
      if (!clientSettings || this.importCanceled) {
        return;
      }

      // Store parsed data and settings for potential real import
      this.parsedData = data;
      this.cachedClientSettings = clientSettings;

      // Run dry-run (preview) — validates all devices without writing to Firestore
      this.importJob = new DeviceImporter(
        data, 
        this.devices,
        this.clientPanels,
        this.fileName ?? '', 
        JSON.parse(JSON.stringify(this.deviceSettingsJson)), 
        clientSettings,
        true,
      )

      this.importJob.observeProgress((progress) => {
        this.progress = (progress.processedDevices / progress.totalDevices) * 100
        this.processedDevices = progress.processedDevices
        this.newDevices = progress.newDevices
        this.updatedDevices = progress.updatedDevices
        this.errors = progress.errors
        this.duplicatedDevices = progress.duplicatedDevices;
        
        if (progress.report) {
          this.importReport = progress.report
        }
        
        if (this.progress >= 100) {
          this.processing = false
          this.previewComplete = true
          this.criticalFailure = progress.criticalFailure
          this.buildPreviewSections()
        }
      })
      await this.importJob.startImport()
    } catch (e) {
      this.cancelUpload()
      this.showSnackbar('settings.hyDryDevice.errorMessageTryAgain')
    }
  }

  /** Runs the real import after the user has reviewed the dry-run preview. */
  async confirmImport() {
    if (!this.parsedData.length || !this.cachedClientSettings) {
      return;
    }

    this.previewComplete = false;
    this.previewSections = [];
    this.processing = true;
    this.progress = 0;
    this.processedDevices = 0;
    this.newDevices = 0;
    this.updatedDevices = 0;
    this.errors = [];
    this.duplicatedDevices = 0;
    this.importReport = [];
    this.criticalFailure = false;

    try {
      // Re-load devices and panels to get fresh state before the real write
      await this.refreshDevicesAndSettings();

      this.importJob = new DeviceImporter(
        this.parsedData,
        this.devices,
        this.clientPanels,
        this.fileName ?? '',
        JSON.parse(JSON.stringify(this.deviceSettingsJson)),
        this.cachedClientSettings,
        false,
      )

      this.importJob.observeProgress((progress) => {
        this.progress = (progress.processedDevices / progress.totalDevices) * 100
        this.processedDevices = progress.processedDevices
        this.newDevices = progress.newDevices
        this.updatedDevices = progress.updatedDevices
        this.errors = progress.errors
        this.duplicatedDevices = progress.duplicatedDevices;
        
        if (progress.report) {
          this.importReport = progress.report
        }
        
        if (this.progress >= 100) {
          this.processing = false
          this.criticalFailure = progress.criticalFailure
          this.importJob?.updateImportHistory(progress)
        }
      })
      await this.importJob.startImport()
    } catch (e) {
      this.cancelUpload()
      this.showSnackbar('settings.hyDryDevice.errorMessageTryAgain')
    }
  }

  /** Maps the dry-run import report to DataViewer sections for the preview. */
  private buildPreviewSections() {
    if (!this.importReport || this.importReport.length === 0) {
      this.previewSections = [];
      return;
    }

    // Report rows may have a 17th error-message field — check if any row has more than 16 fields
    const hasErrors = this.importReport.some(
      (row) => row.split(CSV_SEPARATOR).length > CSV_REPORT_HEADERS.length
    );
    const headers = hasErrors
      ? [...CSV_REPORT_HEADERS, this.$t("settings.hyDryDevice.csvHeaders.error").toString()]
      : CSV_REPORT_HEADERS;

    this.previewSections = [
      mapDeviceCsvToSection(
        headers,
        this.importReport,
        "preview",
        this.fileName ?? this.$t("settings.hyDryDevice.importPreview").toString(),
        true,
        hasErrors,
      ),
    ];
  }

  showHelp() {
    this.showHelpDialog = true
  }

  hideHelp() {
    this.showHelpDialog = false
  }

  removeFile() {
    this.importCanceled = false;

    if (this.uploadElement) {
      this.uploadElement.value = ""
      this.uploadElement.remove()
    }
    this.fileName = null
    this.file = null
  }

  /** Icon of the import result file, which is a CSV. */
  get reportFileTypeIcon(): string {
    return getFileTypeIcon(DownloadFileType.CSV);
  }

  downloadReport() {
    if (!this.importReport) {
      return
    }
    
    const csvContent = [CSV_REPORT_HEADERS.join(CSV_SEPARATOR), ...this.importReport].join('\n');
    const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'});
    const link = document.createElement('a');
    
    const objectUrl = URL.createObjectURL(blob);
    link.href = objectUrl;
    
    const downloadFileName = this.fileName
      ? `${getFileNameWithoutExtension(this.fileName)}-P`
      : `${timestampToDate(firestore.Timestamp.now())}-P`
    link.download = downloadFileName;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
  }

  cancelUpload() {
    this.importJob?.abortImport()
    this.importJob = null
    
    this.importCanceled = true
    this.processing = false
    this.parsedDevices = 0;
  }

  goBack() {
    this.$router.back();
  }

  resetFile() {
    this.removeFile()
    this.progress = 0
    this.errors = []
    this.processing = false
    this.importReport = []
    this.importJob = null
    this.importCanceled = false
    this.previewComplete = false
    this.previewSections = []
    this.parsedData = []
    this.cachedClientSettings = null

    this.refreshDevicesAndSettings();
  }

  /**
   * Holds the "new version" banner back while the import writes devices: a reload part-way through
   * leaves the client's device list half-updated. See `@/utils/UpdateHold`.
   */
  @Watch("processing")
  onProcessingChanged(processing: boolean) {
    setUpdateHold("device-import", processing);
  }

  beforeDestroy() {
    setUpdateHold("device-import", false);
    this.importJob = null
    this.componentActive = false
  }

  mounted() {
    this.setToolbarTitle();
  }

  setToolbarTitle() {
    const baseTitle = this.$t("pages.settings").toString();
    const currentPageTitle = this.$t("settings.hyDryDevice.devicesImport").toString();

    const toolbarTitle = baseTitle + " > " + currentPageTitle;

    ToolbarContentModule.setToolbarTitle(toolbarTitle);
  }
}
